// RailNav.tsx — the rail's grouped nav (handoff README §1 "Grouping" / "Nav item states";
// Shell Anatomy.dc.html §02 "States"). Eight links become three answers to three questions —
// what needs me · what does it know · how does it look — with an ungrouped Overview above
// them. Unanswered is deliberately first in its group (second in the whole rail): the
// honesty loop is the retention mechanism and it used to be the sixth of eight tabs. Do not
// re-sort back to the old tab order (approved deviation, README §1).
import type { LucideIcon } from 'lucide-react';
import {
  CircleHelp,
  FlaskConical,
  Gauge,
  Globe,
  MessageSquare,
  Palette,
  SlidersHorizontal,
  UserRound,
} from 'lucide-react';
import { NavLink } from 'react-router';
import { useBotOverview } from '../../hooks/useBotOverview';
import type { Catalog } from '../../locales/en';
import { useT } from '../../locales/context';

interface NavItemSpec {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Only Overview takes `end` — `/bots/:id` is a path prefix of every other item, so an
   * un-ended NavLink there would stay "active" on every tab. */
  end?: boolean;
  /** Fix-wave item 6 — this used to be `item.label === 'Unanswered'`, coupling the count pill's
   * behaviour to display copy: renaming the label (a copy change) would have silently detached
   * the pill from the item it was built for. An explicit flag on the spec is the fact the
   * rendering code actually cares about. Generalised (triage-state design 2026-08-21 §C) from
   * a single `showsUnansweredCount` boolean into a key naming WHICH `BotOverviewDto.totals`
   * field feeds the pill — Leads' pill counts `newLeads`, not `unansweredGroups`, and a second
   * boolean flag would have made "which of these two is true" a runtime invariant instead of a
   * type-level one. */
  countKey?: 'unansweredGroups' | 'newLeads';
}

interface NavGroup {
  label: string | null;
  items: NavItemSpec[];
}

/** Locale-dashboard plan, Task 4: nav item + group labels moved to `t.shell.*` keys — built from
 * `t` rather than a module-level constant since the nav needs the active catalog. */
function groupsFor(t: Catalog): NavGroup[] {
  return [
    { label: null, items: [{ to: '', label: t.shell.navOverview, icon: Gauge, end: true }] },
    {
      label: t.shell.groupNeedsYou,
      items: [
        { to: '/unanswered', label: t.shell.navUnanswered, icon: CircleHelp, countKey: 'unansweredGroups' },
        { to: '/conversations', label: t.shell.navConversations, icon: MessageSquare },
        { to: '/leads', label: t.shell.navLeads, icon: UserRound, countKey: 'newLeads' },
      ],
    },
    {
      label: t.shell.groupKnowledge,
      items: [
        { to: '/sources', label: t.shell.navSources, icon: Globe },
        { to: '/playground', label: t.shell.navPlayground, icon: FlaskConical },
      ],
    },
    {
      label: t.shell.groupSetup,
      items: [
        { to: '/appearance', label: t.shell.navAppearance, icon: Palette },
        { to: '/settings', label: t.shell.navSettings, icon: SlidersHorizontal },
      ],
    },
  ];
}

// Two sizes of the same item: the rail's 36px (README §1 "Nav item states") and the drawer's
// 44px touch target (README §1 mobile table: "nav items 44px, label 14.5px, icon 17px, count
// pill 20px"). A prop rather than a forked file (task-5-brief.md Step 2 / dispatch: "if a
// component needs a size variant to serve both, add a prop; do not fork the file"). The active
// bar's 22px touch height comes from Dashboard Shell.dc.html's mobile drawer markup, not the
// README table (which is silent on it) — the dispatch says to look at the .dc.html directly.
function itemBase(touch: boolean) {
  return `relative flex items-center rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35 ${
    touch ? 'h-11 gap-[11px] px-2.5 text-[14.5px]' : 'h-9 gap-2.5 px-2.5 text-[13.5px]'
  }`;
}
// Active wash: same formula as the count pill and the "Needs you" spine — color-mix against
// the shared `--st-wash` percentage token (11%/18%), not a new value (README §1 "Active").
const ITEM_ACTIVE = 'bg-[color-mix(in_oklch,var(--primary)_var(--st-wash),transparent)] text-foreground font-semibold';
const ITEM_INACTIVE = 'text-muted-foreground hover:bg-accent';

function CountPill({ count, touch }: { count: number; touch: boolean }) {
  if (count < 1) return null;
  return (
    <span
      className={`grid shrink-0 place-items-center rounded-full font-semibold text-st-paused ${
        touch ? 'h-5 min-w-5 px-[6px] text-[11.5px]' : 'h-[19px] min-w-[19px] px-[5px] text-[11px]'
      }`}
      style={{ backgroundColor: 'color-mix(in oklch, var(--st-paused) var(--st-wash), transparent)' }}
    >
      {count}
    </span>
  );
}

function NavItem({
  spec,
  botId,
  count,
  touch = false,
}: {
  spec: NavItemSpec;
  botId: string | undefined;
  count?: number;
  touch?: boolean;
}) {
  const Icon = spec.icon;
  const iconSize = touch ? 17 : 16;
  const base = itemBase(touch);

  // Zero-bot workspaces (or any route the rail renders on before a bot exists) have nowhere
  // for these links to go — render them inert rather than pointing at `/bots/undefined/...`.
  if (!botId) {
    return (
      <span aria-disabled="true" className={`${base} cursor-default text-muted-foreground/50`}>
        <Icon size={iconSize} strokeWidth={1.9} className="shrink-0" />
        <span className="flex-1 truncate">{spec.label}</span>
      </span>
    );
  }

  return (
    <NavLink
      to={`/bots/${botId}${spec.to}`}
      end={spec.end}
      className={({ isActive }) => `${base} ${isActive ? ITEM_ACTIVE : ITEM_INACTIVE}`}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden="true"
              className={`absolute top-1/2 -left-3 w-[3px] -translate-y-1/2 rounded-r-[3px] bg-primary ${touch ? 'h-[22px]' : 'h-5'}`}
            />
          )}
          <Icon size={iconSize} strokeWidth={1.9} className={`shrink-0 ${isActive ? 'text-primary' : ''}`} />
          <span className="flex-1 truncate">{spec.label}</span>
          {count != null && <CountPill count={count} touch={touch} />}
        </>
      )}
    </NavLink>
  );
}

/** Group micro-labels: JetBrains Mono 10px/500, tracking 0.1em, muted-foreground at 85%
 * opacity (README §1) — three of these break eight links into three answers instead of a
 * flat list. */
function GroupLabel({ label }: { label: string }) {
  return (
    <div className="px-2.5 pb-1 font-mono text-[10px] font-medium tracking-[0.1em] text-muted-foreground/85">
      {label}
    </div>
  );
}

export function RailNav({ botId, touch = false }: { botId: string | undefined; touch?: boolean }) {
  // Same query key `useBotOverview` uses elsewhere — the rail is a second reader of the
  // Overview screen's cache entry, not a new fetch (`enabled: !!botId` already built in).
  const { data: overview } = useBotOverview(botId);
  const t = useT();
  const groups = groupsFor(t);

  return (
    <div className="flex flex-col gap-2">
      {groups.map((group, i) => (
        <div key={group.label ?? 'overview'} className={`flex flex-col gap-0.5 ${i > 0 ? 'mt-1' : ''}`}>
          {group.label && <GroupLabel label={group.label} />}
          {group.items.map((item) => (
            <NavItem
              key={item.to}
              spec={item}
              botId={botId}
              count={item.countKey ? overview?.totals[item.countKey] : undefined}
              touch={touch}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
